// @ts-nocheck
import React, { useRef, useCallback, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { Button, Checkbox, Box, TextField, Alert, DateTimeStandard } from "@curves/ui-base";
import { GradientColorHeader, ReadTextField, ProductInfo, ScrollableTable } from '@curves/ui-shared';
// TODO: 改為庫存與 icoach 共用的 EmployeeSelect 後統一匯入路徑
import { EmployeeSelect } from "@icoach/components";
import { callRef, isFunction, ensureArray, refIsRequiredError } from "@util/utils";
import { getNowTime, parseDate } from "@util/moment";
import useSelection from "@util/hook/useSelection";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import useClubStockOutApi from "@apis/useClubStockOutApi";

// 功能：建立瑕疵退貨 payload item
const toReturnItem = row => ({
	clubDefectiveReportID: Number(row?.clubDefectiveReportID),
});

// 功能：瑕疵退貨清單欄位定義（含勾選欄）
const buildHeaderRow = ({ isAllSelected, isIndeterminate, toggleSelectAll }) => [
	{
		cellKey: "select",
		headerLabel: <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} onChange={toggleSelectAll} onClick={e => e.stopPropagation()} />,
		minWidth: 48,
		align: "center",
		className: "p-0",
		formatCell: (_, row, { selectedIds, toggleSelection }) => {
			if (!selectedIds) return null;
			const id = row?.clubDefectiveReportID;
			if (id == null) return null;
			return <Checkbox checked={selectedIds.has(id)} onChange={() => toggleSelection(id)} onClick={e => e.stopPropagation()} />;
		},
	},
	{
		cellKey: "productInfo",
		headerLabel: "商品資訊",
		minWidth: 200,
		formatCell: (_, row) => (
			<ProductInfo
				product={{
					sku: row.sku,
					name: row.productName,
					spec: row.variantName ?? "",
				}}
				className=""
				showEditIcon={false}
				onEdit={() => {}}
			/>
		),
	},
	{
		cellKey: "reportDate",
		headerLabel: "回報日期",
		minWidth: 110,
		align: "center",
		formatCell: value => (value ? parseDate(value, DateTimeStandard.DisplayDateBySlash) : "—"),
	},
	{
		cellKey: "employeeName",
		headerLabel: "回報人員",
		minWidth: 120,
		align: "center",
		formatCell: value => value ?? "—",
	},
	{
		cellKey: "reportReason",
		headerLabel: "瑕疵狀況說明",
		minWidth: 220,
		formatCell: value => value ?? "—",
	},
];

/**
 * 瑕疵退貨單彈窗
 * Props: open, onClose, refresh
 * 商品清單由 getClubStockOutDefectiveItemsApi 取得（店鋪所有未處理瑕疵）。
 */
const DefectReturnDialog = ({ open, onClose, refresh }) => {
	// 功能：表單欄位 ref（建單人、備註）
	const creatorRef = useRef(null);
	// 功能：非必填原因（獨立欄位，避免與備註混用）
	const reasonRef = useRef(null);
	const noteRef = useRef(null);
	const { employeeOption } = useGetEmployeeOptionState();

	// 功能：未處理瑕疵清單（右側表格資料來源）
	const [defectiveList, setDefectiveList] = useState([]);

	const { getClubStockOutDefectiveItemsApi, postClubStockOutReturnApi } = useClubStockOutApi();

	// 功能：Dialog 開啟時載入未處理瑕疵清單
	useEffect(() => {
		let isCancelled = false;
		const fetchDefectiveItems = async () => {
			if (!open) {
				setDefectiveList([]);
				return;
			}
			try {
				const resp = await getClubStockOutDefectiveItemsApi();
				if (!isCancelled) setDefectiveList(ensureArray(resp));
			} catch (e) {
				if (isCancelled) return;
				setDefectiveList([]);
			}
		};
		fetchDefectiveItems();
		return () => {
			isCancelled = true;
		};
	}, [open, getClubStockOutDefectiveItemsApi]);

	// 功能：表格資料（直接使用後端回傳 shape）
	const displayList = useMemo(() => {
		return ensureArray(defectiveList);
	}, [defectiveList]);

	// 功能：清單勾選狀態
	const selection = useSelection(displayList, "clubDefectiveReportID");
	const { selectedIds, selectedCount, selectedItems, isAllSelected, toggleSelection, toggleSelectAll, clearSelection } = selection;
	const isIndeterminate = selectedCount > 0 && !isAllSelected;

	const cellRenderContext = { selectedIds, isAllSelected, isIndeterminate, toggleSelection, toggleSelectAll };

	// 功能：關閉時清空清單與勾選狀態
	const handleClose = useCallback(
		e => {
			setDefectiveList([]);
			clearSelection();
			if (isFunction(onClose)) onClose(e);
		},
		[onClose, clearSelection],
	);

	// 功能：點選 row 切換勾選
	const handleRowClick = useCallback(
		row => {
			const id = row?.clubDefectiveReportID;
			if (id != null) toggleSelection(id);
		},
		[toggleSelection],
	);

	// 功能：表格欄位定義
	const headerRow = useMemo(() => buildHeaderRow({ isAllSelected, isIndeterminate, toggleSelectAll }), [isAllSelected, isIndeterminate, toggleSelectAll]);

	// 功能：驗證必填 → 組 payload → 送出建立瑕疵退貨單，成功則刷新並關閉
	const handleSubmit = useCallback(
		async e => {
			if (e?.preventDefault) e.preventDefault();

			// 驗證：必填欄位（建單人、備註）
			if (refIsRequiredError(creatorRef, noteRef)) return;

			const payload = {
				stockOutDate: getNowTime(),
				employeeID: Number(callRef(creatorRef, "getResult") || 0),
				reason: String(callRef(reasonRef, "getResult") || "").trim() || undefined,
				notes: String(callRef(noteRef, "getResult") || "").trim(),
				items: ensureArray(selectedItems).map(toReturnItem),
			};

			const resp = await postClubStockOutReturnApi(payload);
			if (resp) {
				isFunction(refresh) && refresh();
				handleClose(e);
			}
		},
		[selectedItems, postClubStockOutReturnApi, refresh, handleClose],
	);

	// 功能：未勾選商品時不可送出
	const isSubmitDisabled = selectedCount === 0;

	if (!open) return null;

	return (
		<Dialog className="simple-content dialog" PaperProps={{ className: "wx-90rem" }} open={open} onClose={handleClose} fullWidth maxWidth="md">
			<GradientColorHeader onClose={handleClose}>瑕疵退貨單</GradientColorHeader>
			<DialogContent className="p-3">
				<Grid container spacing={3}>
					{/* 左欄：表單（建單日期、建單人、收貨對象） */}
					<Grid item xs={12} md={4}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<ReadTextField label="建單日期">{parseDate(new Date(), DateTimeStandard.DisplayDateBySlash)}</ReadTextField>
							</Grid>
							<Grid item xs={12}>
								<EmployeeSelect label="建單人" ref={creatorRef} options={employeeOption} fullWidth required />
							</Grid>
							<Grid item xs={12}>
								<ReadTextField label="收貨對象">總部</ReadTextField>
							</Grid>
							<Grid item xs={12}>
								<TextField label="原因" ref={reasonRef} placeholder="請填寫原因" fullWidth />
							</Grid>
							<Grid item xs={12}>
								<TextField label="備註" ref={noteRef} placeholder="給總部人員看的備註" rows={2} multiline fullWidth />
							</Grid>
						</Grid>
					</Grid>
					{/* 右欄：商品清單 */}
					<Grid item xs={12} md={8}>
						<Box sx={{ minHeight: 300 }}>
							{displayList.length === 0 ? (
								<Alert className="mb-2" severity="info" variant="filled" color="info">
									目前沒有未處理的瑕疵回報。
								</Alert>
							) : null}
							<ScrollableTable
								headerRow={headerRow}
								rowsData={displayList}
								rowKey="clubDefectiveReportID"
								className="w-100"
								rowOnClick={handleRowClick}
								rowOnCheckActiveState={row => row?.clubDefectiveReportID != null && selectedIds.has(row.clubDefectiveReportID)}
								cellRenderContext={cellRenderContext}
								tableClassName="theme-gray"
							/>
						</Box>
					</Grid>
				</Grid>
				<Alert className="mt-3" severity="info" variant="filled" color="info">
					提醒您！本作業僅為處理 iCoach 庫存系統之瑕疵、退貨數量異動，如需跟總部申請退換貨，請務必再至購物平台申請 【退貨與退款】流程。
				</Alert>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled}>
					確認退貨
				</Button>
			</DialogActions>
		</Dialog>
	);
};

DefectReturnDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	refresh: PropTypes.func,
};

export default DefectReturnDialog;
